// b11 Sharlar — kids' circus / carnival birthday preview card.
//
// The full template is an aged-parchment birthday invitation with
// burgundy + gold arches, balloons and bunting flags. The catalog card
// keeps that mood: a burgundy paper-fold frame with gold confetti
// flecks, an inner parchment card, and a focal medallion holding a
// trio of paper-cut balloons under a bunting garland with a numeric
// age stamp. Names in the template font; eyebrow / footer use the
// standard small-caps treatment.
import { type LayoutCtx } from "./_shared";

const FRAME =
  "linear-gradient(155deg, #4a0f1a 0%, #7a1a26 55%, #5a1420 100%)";

// Deterministic confetti positions — keeps the catalog card pixel-stable.
const FLECKS = [
  [8, 12], [22, 8], [36, 16], [54, 10], [68, 14], [82, 8], [92, 18],
  [6, 38], [88, 40], [12, 60], [86, 62], [16, 82], [82, 86],
];

export function SharlarPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const goldLight = "#e8c878";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FRAME,
        padding: "5%",
        display: "flex",
      }}
    >
      {FLECKS.map(([x, y], i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${x}%`,
            top: `${y}%`,
            width: 3, height: 8,
            background: i % 2 ? accent : goldLight,
            borderRadius: 1,
            opacity: 0.8,
            transform: `rotate(${(i % 3 - 1) * 30}deg)`,
          }}
        />
      ))}
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "9% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}66`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TUG'ILGAN KUN
        </div>

        {/* bunting + balloons + age medallion */}
        <div style={{ width: "62%", aspectRatio: "1 / 1", position: "relative", marginBottom: "5%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `1px solid ${accent}66`,
              overflow: "hidden",
              background: bg,
              display: "grid",
              placeItems: "center",
            }}
          >
            <svg viewBox="0 0 100 100" width="92%" height="92%" aria-hidden>
              {/* bunting garland — gentle arc with triangular flags */}
              <path d="M8 22 Q 50 6, 92 22" fill="none" stroke={ink}
                strokeWidth="1.2" opacity="0.6" />
              {[16, 28, 40, 50, 60, 72, 84].map((x, i) => {
                const y = 22 - Math.sin((x - 8) / 84 * Math.PI) * 14;
                const col = i % 3 === 0 ? accent : i % 3 === 1 ? "#7a1a26" : goldLight;
                return (
                  <polygon
                    key={i}
                    points={`${x - 3.2},${y + 1} ${x + 3.2},${y + 1} ${x},${y + 8}`}
                    fill={col}
                  />
                );
              })}

              {/* three balloons with strings */}
              <g>
                {/* left balloon — accent gold */}
                <ellipse cx="28" cy="50" rx="9" ry="11" fill={accent} />
                <polygon points="28,61 26.5,63 29.5,63" fill={accent} />
                <path d="M28 63 Q 27 72, 30 80" fill="none" stroke={ink}
                  strokeWidth="0.6" opacity="0.5" />
                {/* right balloon — burgundy */}
                <ellipse cx="72" cy="46" rx="9" ry="11" fill="#7a1a26" />
                <polygon points="72,57 70.5,59 73.5,59" fill="#7a1a26" />
                <path d="M72 59 Q 73 70, 70 80" fill="none" stroke={ink}
                  strokeWidth="0.6" opacity="0.5" />
                {/* center balloon — gold-light, larger, anchored low */}
                <ellipse cx="50" cy="56" rx="11" ry="13" fill={goldLight} />
                <polygon points="50,69 48,71 52,71" fill={goldLight} />
                <path d="M50 71 Q 50 80, 50 88" fill="none" stroke={ink}
                  strokeWidth="0.6" opacity="0.5" />
              </g>

              {/* a couple of stars in the negative space */}
              {[[14, 70], [86, 72]].map(([cx, cy], i) => (
                <path key={i}
                  d={`M${cx} ${cy - 2.6} l1 2.1 2.3.3-1.7 1.6.4 2.3-2-1.1-2 1.1.4-2.3-1.7-1.6 2.3-.3z`}
                  fill={accent}
                />
              ))}
            </svg>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 30, lineHeight: 1.06, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: accent,
            marginTop: "5%",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
